/*
 * Nexo: product page behaviour (sections/nexo-product.liquid).
 * - <nexo-gallery>: photo stage, thumbnails, arrows, swipe, full-screen photos.
 * - <nexo-buy>: quantity (capped at real stock minus what's already in the cart),
 *   "Agregar al carrito", "Comprar ahora", the phone buy bar.
 * - <nexo-bundle>: "Completá tu equipo" total and add-all.
 * - <nexo-reviews>: shows "Escribir una opinión" only when Judge.me is running.
 * - <nexo-more>: long description and specifications collapsed with "Ver más".
 * - Versions picker: switches colour / storage / model in place (no reload).
 * Adds dispatch the same cart event as Horizon's product form, so the cart drawer, the header
 * count and the fly-to-cart animation react exactly as they do elsewhere in the theme.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const ROOT = window.Shopify?.routes?.root || '/';

async function fetchCart() {
  const res = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } });
  return res.json();
}

/**
 * Adds lines to the cart and tells the rest of the theme about it.
 * @param {{id: string, quantity: number}[]} lines
 * @param {HTMLElement} source - element the cart event is dispatched from
 * @returns {Promise<{ok: boolean, message?: string, cart?: any}>}
 */
async function addToCart(lines, source) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
  const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();

  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: lines.map((line) => ({ id: Number(line.id), quantity: line.quantity })),
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await res.json();
    const failed = !res.ok || Boolean(data.status);
    const cart = await fetchCart();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'nexo-product',
        itemCount,
        sections: failed ? undefined : data.sections,
        didError: failed,
      },
    });

    if (failed) {
      return { ok: false, cart, message: data.description || data.message || 'No se pudo agregar al carrito.' };
    }
    return { ok: true, cart };
  } catch (error) {
    deferred.reject(error);
    return { ok: false, message: 'No se pudo agregar al carrito. Revisá tu conexión e intentá de nuevo.' };
  }
}

/* ---------- gallery ---------- */

class NexoGallery extends HTMLElement {
  connectedCallback() {
    this.images = [...this.querySelectorAll('.nx-stage__img')];
    this.count = this.images.length;
    this.index = Number(this.dataset.start) || 0;
    this.swipedAt = 0;
    this.lightbox = this.querySelector('[data-lightbox]');
    this.thumbs = this.querySelector('.nx-thumbs');

    this.addEventListener('click', this.onClick);
    this.bindSwipe(this.querySelector('.nx-stage'));
    // The next and previous photos start loading as soon as a pointer or finger reaches the gallery
    if (this.count > 1) this.addEventListener('pointerenter', () => this.warm(), { once: true });

    if (this.lightbox) {
      this.bindSwipe(this.lightbox);
      this.lightbox.addEventListener('click', (event) => {
        if (event.target === this.lightbox) this.lightbox.close();
      });
      this.lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.go(this.index + 1);
        if (event.key === 'ArrowLeft') this.go(this.index - 1);
      });
      this.lightbox.addEventListener('close', () => {
        document.documentElement.classList.remove('nx-lock');
      });
    }
  }

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-photo], [data-step], [data-zoom], [data-lb-close]') : null;
    if (!target) return;
    if (target.hasAttribute('data-photo')) this.go(Number(target.dataset.photo));
    else if (target.hasAttribute('data-step')) this.go(this.index + Number(target.dataset.step));
    else if (target.hasAttribute('data-lb-close')) this.lightbox?.close();
    else if (target.hasAttribute('data-zoom') && Date.now() - this.swipedAt > 400) this.open();
  };

  bindSwipe(el) {
    if (!el || this.count < 2) return;
    let startX = null;
    let startY = 0;
    el.addEventListener(
      'touchstart',
      (event) => {
        // One finger only: two are a pinch zoom
        if (event.touches.length > 1) {
          startX = null;
          return;
        }
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      },
      { passive: true }
    );
    el.addEventListener(
      'touchend',
      (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        startX = null;
        // Mostly sideways only, so scrolling the page down past the photo never changes it
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.swipedAt = Date.now();
          this.go(this.index + (dx < 0 ? 1 : -1));
        }
      },
      { passive: true }
    );
  }

  go(k) {
    if (!this.count) return;
    this.index = (k + this.count) % this.count;
    const img = this.images[this.index];
    this.warm();

    // The photos crossfade in CSS (.nx-stage__img). A photo that isn't decoded yet would fade in
    // as nothing and then pop, so wait for it, but never more than 300 ms.
    const turn = (this.turn = (this.turn || 0) + 1);
    const show = () => {
      if (turn !== this.turn) return;
      this.images.forEach((el, i) => el.classList.toggle('is-active', i === this.index));
    };
    if (img.complete || typeof img.decode !== 'function') show();
    else Promise.race([img.decode(), new Promise((r) => setTimeout(r, 300))]).then(show, show);

    const label = this.querySelector('[data-count-label]');
    if (label) label.textContent = `${this.index + 1} / ${this.count}`;
    if (this.thumbs) {
      const buttons = [...this.thumbs.querySelectorAll('[data-photo]')];
      buttons.forEach((b, i) => b.setAttribute('aria-pressed', String(i === this.index)));
      const current = buttons[this.index];
      if (current) {
        this.thumbs.scrollTo({
          top: current.offsetTop - (this.thumbs.clientHeight - current.offsetHeight) / 2,
          left: current.offsetLeft - (this.thumbs.clientWidth - current.offsetWidth) / 2,
          behavior: 'smooth',
        });
      }
    }
    if (this.lightbox?.open) this.renderLightbox(true);
  }

  /** Starts loading the current photo and its two neighbours (lazy until then). */
  warm() {
    [0, 1, -1].forEach((step) => {
      this.images[(this.index + step + this.count) % this.count]?.removeAttribute('loading');
    });
  }

  open() {
    if (!this.lightbox || !this.count) return;
    this.renderLightbox(false);
    document.documentElement.classList.add('nx-lock');
    this.lightbox.showModal();
  }

  /**
   * @param {boolean} fade - crossfade from the photo on screen (when moving inside the open viewer)
   */
  renderLightbox(fade) {
    const img = this.images[this.index];
    const big = this.lightbox.querySelector('[data-lb-img]');
    const src = img.dataset.zoomSrc || img.currentSrc || img.src;
    const count = this.lightbox.querySelector('[data-lb-count]');
    if (count) count.textContent = `${this.index + 1} / ${this.count}`;

    const apply = () => {
      big.setAttribute('width', img.getAttribute('width') || '');
      big.setAttribute('height', img.getAttribute('height') || '');
      big.src = src;
      big.alt = img.alt;
    };
    if (!fade || !big.getAttribute('src') || typeof big.animate !== 'function') {
      apply();
      return;
    }

    // Load the big photo first (up to 600 ms), then swap it in under a copy of the old one that
    // fades out on top. The new photo is fully opaque underneath, so nothing dips to white.
    const turn = (this.lbTurn = (this.lbTurn || 0) + 1);
    const next = new Image();
    next.src = src;
    const ready = typeof next.decode === 'function' ? next.decode() : Promise.resolve();
    Promise.race([ready, new Promise((r) => setTimeout(r, 600))])
      .catch(() => {})
      .then(() => {
        if (turn !== this.lbTurn || !this.lightbox.open) return;
        const box = this.lightbox.getBoundingClientRect();
        const rect = big.getBoundingClientRect();
        const ghost = /** @type {HTMLImageElement} */ (big.cloneNode());
        ghost.removeAttribute('data-lb-img');
        ghost.alt = '';
        ghost.setAttribute('aria-hidden', 'true');
        ghost.className = 'nx-lightbox__ghost';
        Object.assign(ghost.style, {
          left: `${rect.left - box.left}px`,
          top: `${rect.top - box.top}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`,
        });
        // First child, so the close button and arrows stay above it
        this.lightbox.prepend(ghost);
        apply();
        ghost
          .animate([{ opacity: 1 }, { opacity: 0 }], { duration: 220, easing: 'ease', fill: 'forwards' })
          .finished.catch(() => {})
          .finally(() => ghost.remove());
      });
  }
}

/* ---------- buy column ---------- */

class NexoBuy extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form.nx-buy-form');
    if (!this.form) return;
    this.qty = this.form.querySelector('[data-qty]');
    this.addButton = this.form.querySelector('[data-add]');
    this.message = this.form.querySelector('[data-msg]');
    this.steps = [...this.form.querySelectorAll('[data-q]')];
    this.barButton = document.querySelector('[data-buybar-add]');
    this.variantId = this.dataset.variantId;
    this.tracked = this.dataset.tracked === 'true';
    this.stock = Number(this.dataset.stock) || 0;
    this.max = this.tracked ? Number(this.dataset.max) : Infinity;
    this.busy = false;

    this.form.addEventListener('submit', this.onSubmit);
    this.form.addEventListener('click', this.onClick);
    this.qty.addEventListener('change', () => this.setQty(this.qty.value));
    this.barButton?.addEventListener('click', () => this.add(this.barButton));
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
  }

  onSubmit = (event) => {
    event.preventDefault();
    this.add(this.addButton);
  };

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const step = target?.closest('[data-q]');
    if (step) {
      this.setQty(Number(this.qty.value) + Number(step.dataset.q));
      return;
    }
    const buyNow = target?.closest('[data-buy-now]');
    if (buyNow) this.buyNow(buyNow);
  };

  setQty(value) {
    const cap = Math.max(1, this.max);
    const n = Math.min(Math.max(1, Math.round(Number(value)) || 1), cap);
    this.qty.value = String(n);
    this.sync();
  }

  sync() {
    const n = Number(this.qty.value) || 1;
    const full = this.max < 1;
    if (this.steps[0]) this.steps[0].disabled = n <= 1 || full;
    if (this.steps[1]) this.steps[1].disabled = n >= this.max || full;
    this.addButton.disabled = full || this.busy;
    this.addButton.textContent = full ? 'Ya tenés todo el stock en el carrito' : this.busy ? 'Agregando…' : 'Agregar al carrito';
    if (this.barButton) this.barButton.disabled = full || this.busy;
    if (Number.isFinite(this.max)) this.qty.max = String(Math.max(1, this.max));
  }

  say(text) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.hidden = !text;
  }

  async add(trigger) {
    if (this.busy || this.max < 1) return;
    const quantity = Number(this.qty.value) || 1;
    this.busy = true;
    this.say('');
    this.sync();
    this.fly(trigger);
    const result = await addToCart([{ id: this.variantId, quantity }], this);
    this.busy = false;
    if (result.cart) this.updateMax(result.cart);
    if (result.ok) this.qty.value = '1';
    else this.say(result.message);
    this.sync();
  }

  async buyNow(button) {
    if (this.busy) return;
    this.busy = true;
    button.disabled = true;
    this.say('');
    this.sync();
    try {
      const cart = await fetchCart();
      const inCart = cart.items.some((item) => String(item.variant_id) === String(this.variantId));
      if (!inCart) {
        const res = await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(this.variantId), quantity: Number(this.qty.value) || 1 }] }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.description || data.message);
        }
      }
      window.location.href = `${ROOT}checkout`;
    } catch (error) {
      this.say(error?.message || 'No se pudo ir a pagar. Intentá de nuevo.');
      button.disabled = false;
      this.busy = false;
      this.sync();
    }
  }

  onCartUpdate = (event) => {
    if (event.target === this) return;
    event.promise
      ?.then(() => fetchCart())
      .then((cart) => {
        this.updateMax(cart);
        this.sync();
      })
      .catch(() => {});
  };

  updateMax(cart) {
    if (!this.tracked) return;
    const inCart = cart.items
      .filter((item) => String(item.variant_id) === String(this.variantId))
      .reduce((sum, item) => sum + item.quantity, 0);
    this.max = this.stock - inCart;
    if (Number(this.qty.value) > Math.max(1, this.max)) this.qty.value = String(Math.max(1, this.max));
  }

  fly(trigger) {
    const destination = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.image;
    if (!trigger || !destination || !image || !customElements.get('fly-to-cart')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = /** @type {any} */ (document.createElement('fly-to-cart'));
    el.classList.add('fly-to-cart--main');
    el.style.setProperty('background-image', `url(${image})`);
    el.style.setProperty('--start-opacity', '0');
    el.source = trigger;
    el.destination = destination;
    document.body.appendChild(el);
  }
}

/* ---------- Completá tu equipo ---------- */

class NexoBundle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-bun-add]');
    this.message = this.querySelector('[data-msg]');
    this.addEventListener('change', (event) => {
      if (event.target instanceof Element && event.target.matches('[data-bun]')) this.update();
    });
    this.button?.addEventListener('click', () => this.add());
  }

  selected() {
    return [...this.querySelectorAll('[data-bun]')].filter((box) => box.checked);
  }

  money(cents, dropZeros) {
    let text = formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
    if (dropZeros && cents % 100 === 0) text = text.replace(/[.,]00(?!\d)/, '');
    return text;
  }

  update() {
    const selected = this.selected();
    this.querySelectorAll('.nx-b-item').forEach((item) => {
      item.classList.toggle('is-on', Boolean(item.querySelector('[data-bun]')?.checked));
    });
    const total = selected.reduce((sum, box) => sum + Number(box.dataset.price), 0);
    const n = selected.length;
    const count = Number(this.dataset.instCount) || 0;
    const min = Number(this.dataset.instMin) || 0;

    this.querySelector('[data-bun-label]').textContent = n === 1 ? 'Total por este producto' : `Total por los ${n}`;
    // The store's money format can carry markup (<span class="nx-cur">U$S</span>), so prices go in
    // as HTML. It comes from Settings → General, not from shoppers.
    this.querySelector('[data-bun-total]').innerHTML = this.money(total);
    const inst = this.querySelector('[data-bun-inst]');
    if (inst) {
      inst.innerHTML = count > 1 && total >= min ? `${count} cuotas de ${this.money(Math.ceil(total / count / 100) * 100, true)}` : '';
    }
    this.button.textContent = n === 1 ? 'Agregar al carrito' : `Agregar los ${n} al carrito`;
  }

  async add() {
    const lines = this.selected().map((box) => ({ id: box.dataset.variant, quantity: 1 }));
    if (!lines.length) return;
    this.button.disabled = true;
    if (this.message) this.message.hidden = true;
    const result = await addToCart(lines, this);
    this.button.disabled = false;
    if (!result.ok && this.message) {
      this.message.textContent = result.message;
      this.message.hidden = false;
    }
  }
}

/* ---------- reviews ---------- */

class NexoReviews extends HTMLElement {
  connectedCallback() {
    const write = this.querySelector('[data-write]');
    const widget = this.querySelector('.nx-jdgm');
    if (!write || !widget) return;

    const judgeMeRunning = () => Boolean(window.jdgm) || Boolean(document.querySelector('script[src*="judge.me"], script[src*="judgeme"]'));
    const reveal = () => {
      if (!judgeMeRunning()) return;
      const cta = this.querySelector('.nx-op-empty__cta');
      if (cta) cta.hidden = false;
      write.hidden = false;
    };
    if (document.readyState === 'complete') reveal();
    else window.addEventListener('load', reveal, { once: true });

    write.addEventListener('click', () => {
      const empty = this.querySelector('[data-empty]');
      if (empty) empty.hidden = true;
      widget.hidden = false;
      widget.querySelector('.jdgm-write-rev-link')?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });
  }
}

/* ---------- versions (snippets/nexo-pdp-versions.liquid) ---------- */

/*
 * Picking another colour, storage or model swaps the product sections in place with the
 * Section Rendering API instead of reloading: no jump to the top, same scroll position, the URL
 * and title follow. Pages are prefetched on hover or focus. Any error falls back to a normal
 * navigation, and the theme editor always navigates normally.
 */
const versionCache = new Map();

function productSections() {
  return [...document.querySelectorAll('main > .shopify-section')].filter((el) => el.id.startsWith('shopify-section-'));
}

function fetchVersion(url) {
  if (versionCache.has(url)) return versionCache.get(url);
  const ids = productSections().map((el) => el.id.replace(/^shopify-section-/, ''));
  const target = new URL(url, window.location.origin);
  target.searchParams.set('sections', ids.join(','));
  // No "Accept: application/json" here: on product URLs Shopify would answer with the product data
  const request = fetch(target).then((res) => {
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  });
  request.catch(() => versionCache.delete(url));
  versionCache.set(url, request);
  return request;
}

async function showVersion(url, { push = true, focusRow = '' } = {}) {
  const busy = document.querySelector('[data-nx-versions]');
  busy?.setAttribute('aria-busy', 'true');
  try {
    const sections = await fetchVersion(url);
    for (const el of productSections()) {
      const html = sections[el.id.replace(/^shopify-section-/, '')];
      if (!html) continue;
      const fresh = new DOMParser().parseFromString(html, 'text/html').body.firstElementChild;
      if (fresh) el.replaceWith(fresh);
    }
    if (push) window.history.pushState({ nexoVersion: true }, '', url);
    const heading = document.querySelector('.nx-buy__title')?.textContent?.trim();
    if (heading) {
      const suffix = document.title.includes(' – ') ? document.title.slice(document.title.lastIndexOf(' – ')) : '';
      document.title = heading + suffix;
    }
    if (focusRow) {
      const current = document.querySelector(`[data-nx-versions] [role="group"][aria-label="${CSS.escape(focusRow)}"] [aria-current="true"]`);
      if (current instanceof HTMLElement) current.focus({ preventScroll: true });
    }
  } catch {
    window.location.href = url;
  }
}

function versionLink(event) {
  const link = event.target instanceof Element ? event.target.closest('[data-nx-versions] a[href]') : null;
  return link instanceof HTMLAnchorElement ? link : null;
}

if (!window.nexoVersionsReady && !window.Shopify?.designMode) {
  window.nexoVersionsReady = true;

  document.addEventListener('click', (event) => {
    const link = versionLink(event);
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    if (link.getAttribute('aria-current') === 'true') return;
    showVersion(link.href, { focusRow: link.closest('[role="group"]')?.getAttribute('aria-label') || '' });
  });

  const prefetch = (event) => {
    const link = versionLink(event);
    if (link && link.getAttribute('aria-current') !== 'true') fetchVersion(link.href).catch(() => {});
  };
  document.addEventListener('pointerover', prefetch, { passive: true });
  document.addEventListener('focusin', prefetch);

  window.history.replaceState({ nexoVersion: true }, '', window.location.href);
  window.addEventListener('popstate', (event) => {
    if (event.state?.nexoVersion && document.querySelector('[data-nx-versions]')) {
      showVersion(window.location.href, { push: false });
    }
  });
}

/**
 * <nexo-more>: collapses a long description or specs table to --nx-more-h with "Ver más" / "Ver menos".
 * Only when there's a good amount hidden (over 40% more than the cut), so it never hides 2 lines.
 * The specs (data-more-match) line up with the description column on desktop.
 */
class NexoMore extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('.nx-more__body');
    this.btn = this.querySelector('.nx-more__btn');
    this.label = this.querySelector('[data-more-label]');
    if (!this.body || !this.btn || this.btn.dataset.ready) return;
    this.btn.dataset.ready = '1';
    this.btn.addEventListener('click', () => this.toggle());
    this.measure();
    // Web fonts change line heights; measure again once they're in
    document.fonts?.ready.then(() => this.measure());
    // Measure again when the layout width changes (window resized, columns stacking)
    if ('ResizeObserver' in window && this.parentElement) {
      let lastWidth = 0;
      this.ro = new ResizeObserver(([entry]) => {
        const width = Math.round(entry.contentRect.width);
        if (width === lastWidth) return;
        lastWidth = width;
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.measure(), 100);
      });
      this.ro.observe(this.parentElement);
    }
  }

  disconnectedCallback() {
    this.ro?.disconnect();
  }

  /**
   * With data-more-match (the specs), when this sits beside the previous column (desktop): the
   * height at which "Ver más" ends where that column's content ends. 0 when stacked (phone).
   * The cut is never below --nx-more-h.
   */
  matchedCut() {
    const other = this.hasAttribute('data-more-match') ? this.previousElementSibling : null;
    const otherEnd = other?.lastElementChild;
    if (!otherEnd) return 0;
    if (Math.abs(this.getBoundingClientRect().top - other.getBoundingClientRect().top) > 4) return 0;
    const wasHidden = this.btn.hidden;
    this.btn.hidden = false;
    const btnSpace = this.btn.getBoundingClientRect().bottom - this.body.getBoundingClientRect().bottom;
    this.btn.hidden = wasHidden;
    return Math.floor(otherEnd.getBoundingClientRect().bottom - this.body.getBoundingClientRect().top - btnSpace);
  }

  measure() {
    if (this.classList.contains('is-open')) return;
    this.style.removeProperty('--nx-more-cut');
    const base = parseFloat(getComputedStyle(this).getPropertyValue('--nx-more-h')) || 300;
    const matched = this.matchedCut();
    const cut = Math.max(base, matched);
    // Hide at least a few rows (150px) when matching a column, 40% of the cut otherwise
    const hidden = this.body.scrollHeight - cut;
    const long = matched > base ? hidden > 150 : hidden > cut * 0.4;
    this.classList.toggle('is-long', long);
    this.btn.hidden = !long;
    if (long) this.style.setProperty('--nx-more-cut', `${cut}px`);
  }

  toggle() {
    const open = !this.classList.contains('is-open');
    const body = this.body;
    if (open) {
      body.style.maxHeight = `${body.scrollHeight}px`;
      this.classList.add('is-open');
      const done = () => {
        if (this.classList.contains('is-open')) body.style.maxHeight = 'none';
      };
      if (parseFloat(getComputedStyle(body).transitionDuration) > 0) {
        body.addEventListener('transitionend', done, { once: true });
        setTimeout(done, 500); // in case transitionend never comes (background tab)
      } else {
        done();
      }
    } else if (this.getBoundingClientRect().top < 0) {
      // Scrolled past the start: close at once and jump back to the title, so the page doesn't
      // shrink under the shopper while it animates
      body.style.transition = 'none';
      this.classList.remove('is-open');
      body.style.maxHeight = '';
      this.scrollIntoView({ block: 'start', behavior: 'instant' });
      void body.offsetHeight;
      body.style.transition = '';
    } else {
      body.style.maxHeight = `${body.scrollHeight}px`;
      void body.offsetHeight;
      this.classList.remove('is-open');
      body.style.maxHeight = '';
    }
    this.btn.setAttribute('aria-expanded', String(open));
    if (this.label) this.label.textContent = open ? 'Ver menos' : 'Ver más';
  }
}

if (!customElements.get('nexo-gallery')) customElements.define('nexo-gallery', NexoGallery);
if (!customElements.get('nexo-more')) customElements.define('nexo-more', NexoMore);
if (!customElements.get('nexo-buy')) customElements.define('nexo-buy', NexoBuy);
if (!customElements.get('nexo-bundle')) customElements.define('nexo-bundle', NexoBundle);
if (!customElements.get('nexo-reviews')) customElements.define('nexo-reviews', NexoReviews);
